import type { CatalogStar, SkyCatalog } from "./types";

export const CatalogUrl = "/catalog/catalog.v1.json";
export const CatalogChecksumUrl = "/catalog/catalog.v1.sha256";

export interface CatalogLoadResult {
  catalog: SkyCatalog;
  raw: string;
  expectedSha256: string;
  verified: boolean;
}

export async function loadCatalog(fetcher: typeof fetch = globalThis.fetch): Promise<CatalogLoadResult> {
  const [raw, expectedSha256] = await Promise.all([
    fetchText(fetcher, CatalogUrl),
    fetchText(fetcher, CatalogChecksumUrl),
  ]);
  const catalog = parseCatalog(raw);
  const checksum = expectedSha256.trim();
  return {
    catalog,
    raw,
    expectedSha256: checksum,
    verified: await verifyCatalogChecksum(raw, checksum),
  };
}

export function parseCatalog(raw: string): SkyCatalog {
  const catalog = JSON.parse(raw) as SkyCatalog;
  if (catalog.v !== 1) {
    throw new Error(`Unsupported catalog version: ${catalog.v}`);
  }
  const seen = new Set<number>();
  for (const star of catalog.stars) {
    if (!Number.isInteger(star.hr) || star.hr <= 0) {
      throw new Error(`Invalid HR number in catalog: ${star.hr}`);
    }
    if (seen.has(star.hr)) {
      throw new Error(`Duplicate HR number in catalog: ${star.hr}`);
    }
    seen.add(star.hr);
  }
  return catalog;
}

export async function verifyCatalogChecksum(raw: string, expectedSha256: string): Promise<boolean> {
  return (await sha256Hex(raw)) === expectedSha256.trim();
}

export async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function starByHr(catalog: SkyCatalog, hr: number): CatalogStar | undefined {
  return catalog.stars.find((star) => star.hr === hr);
}

async function fetchText(fetcher: typeof fetch, url: string): Promise<string> {
  const response = await fetcher(url);
  if (!response.ok) {
    throw new Error(`Could not load ${url}: ${response.status} ${response.statusText}`);
  }
  return response.text();
}
